import { DatePipe, SlicePipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { dsDtos, webDtos } from '@vectronic/openApi-admin';
import { userDtos, webEnums } from '@vectronic/openApi-customer';
import {
  DialogService,
  EventRequestDto,
  getCurrentTimespan,
  Item,
  ItemHelp,
  latestBuild,
  Translator,
} from '@vectronic/util';
import moment from 'moment/moment';
import { Subscription } from 'rxjs';
import { EditDeviceComponent } from '../../modals/edit-collar/edit-device.component';
import { DetailsItemComponent } from '../details-item/details-item.component';

@Component({
  selector: 'vectronic-collar-details',
  templateUrl: './collar-details.component.html',
  styleUrls: ['./collar-details.component.scss'],
  imports: [
    DatePipe,
    SlicePipe,
    DetailsItemComponent,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class CollarDetailsComponent<T extends webDtos.CollarExtended | webDtos.CollarAdmin> {
  animalSpecie = input<dsDtos.AnimalSpecies>();
  showExtraInfo = input<boolean>(false);
  deviceTypes = input<dsDtos.DeviceType[]>();
  collar = input<T>();
  collarGroups = input<userDtos.Group[]>();
  movebankAccount = input<webDtos.MovebankAccount>();
  showMore = input<boolean>(false);
  showLink = input<boolean>(false);
  inMobileView = input<boolean>(false);
  currentDeployment = input<userDtos.Deployment>();
  animalLastPosition = input<webDtos.GPSPosition>();

  lastPosition = computed(() => {
    if (this.animalLastPosition()) return this.animalLastPosition();
    if (this.isAdminCollar()) return this.adminCollar()?.lastPosition;
    else {
      const {
        lastPositionLatitude: latitude,
        lastPositionAcquisitionTime: acquisitionTime,
        lastPositionBackupVoltage: backupVoltage,
        lastPositionLongitude: longitude,
        lastPositionMainVoltage: mainVoltage,
        lastPositionMortalityStatus: mortalityStatus,
      } = this.customerCollar();
      return { latitude, longitude, acquisitionTime, backupVoltage, mainVoltage, mortalityStatus };
    }
  });
  openCreateDeploymentModal = output();
  syncCollarWithMovebankClicked = output<{ id: number }>();
  toggleMovebankDevice = output<{ id: number; isInMovebank: boolean }>();
  updateCollar = output<T>();
  loadCollarsPositions = output<{ request: EventRequestDto; collarIds: number[] }>();

  mapPosList: webDtos.GPSPosition[];
  positionCount: number;
  subscription: Subscription;

  private dialogService = inject(DialogService);
  private router = inject(Router);

  getCurrentTimespan = getCurrentTimespan;

  beaconFrequency = computed(() => this.customerCollar().beaconFrequency);

  deviceTypeName = computed(() => {
    const deviceType = this.deviceTypes()?.find(
      (deviceType) => deviceType.id === this.collar().deviceTypeId,
    );
    return deviceType?.customerName;
  });

  isCustomerCollar = computed(() => !this.isAdminCollar());
  customerCollar = computed(() => this.collar() as webDtos.CollarExtended);
  isAdminCollar = computed(() => 'idDevice' in this.collar());
  adminCollar = computed(() => this.collar() as webDtos.CollarAdmin);
  build = computed(() => latestBuild(this.adminCollar()));

  comId = computed(() => {
    if (this.isCustomerCollar()) return this.customerCollar().comId;
    return getCurrentTimespan(this.adminCollar().comIdTimeline)?.data;
  });

  openCreateCollarModal() {
    this.dialogService.open(EditDeviceComponent, {
      data: this.collar() as webDtos.CollarExtended,
      afterConfirm: (collar) => this.updateCollar.emit(collar as T),
    });
  }

  syncCollarWithMovebank(canSync: boolean, id: number) {
    if (canSync) this.syncCollarWithMovebankClicked.emit({ id });
  }

  movebankToggle(
    event: MatSlideToggleChange,
    canToggle: boolean,
    id: number,
    isInMovebank: boolean,
  ) {
    if (!canToggle) return;
    event.source.checked = this.isMovebankSyncEnabled();
    this.toggleMovebankDevice.emit({ id, isInMovebank });
  }

  translateComType(comType: webEnums.ComType) {
    return Translator.comType(comType);
  }

  navigateToCollar() {
    this.router.navigate([ItemHelp.toRoute(Item.COLLAR, this.collarId())]);
  }

  isReader = computed(() =>
    this.isCustomerCollar() ? this.customerCollar().role === 'READER' : false,
  );

  movebankAccountExists = computed(() => Boolean(this.movebankAccount()?.movebankUsername));
  movebankSliderShouldBeDisabled = computed(() => this.isReader() || !this.movebankAccountExists());
  isMovebankSyncEnabled = computed(() =>
    this.movebankAccount()
      ?.deviceList?.filter(({ forwardData }) => forwardData)
      ?.map(({ idDevice }) => idDevice)
      ?.includes(this.collarId()),
  );
  canSyncWithMovebank = computed(() => !this.isReader() && this.movebankAccountExists());

  collarId = computed(() => {
    if (this.isCustomerCollar()) return this.customerCollar().id;
    else return this.adminCollar().idDevice;
  });

  collarPositionCount = computed(() =>
    this.isCustomerCollar() ? this.customerCollar().positionCount : this.positionCount,
  );

  roundPosition(lastPosition: webDtos.GPSPosition): string {
    return Translator.roundPosition(lastPosition);
  }

  getRoundNumber(number: number) {
    return Translator.getRoundNumber(number);
  }

  translateMortalityStatusShort(lastPositionMortalityStatus: webEnums.MortalityStatus) {
    return Translator.mortalityStatusShort(lastPositionMortalityStatus);
  }

  collarLastReception = computed(() =>
    'lastReception' in this.collar() ? this.customerCollar().lastReception : undefined,
  );

  getDeploymentDateRange(deployment: userDtos.Deployment) {
    return Translator.deploymentDateRange(deployment);
  }

  toFormattedDate(date: string): string {
    if (!date) return '';
    return moment(date).utc(true).format('YYYY-MM-DD');
  }
}
